import * as React from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Divider,
} from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useApiClient } from "../../../api";
import Autocomplete from "@mui/material/Autocomplete";
import { enqueueSnackbar } from "notistack";
import { LicenseUsageTable } from "./LicenseUsageTable";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import { useSchoolSubscriptionTypes } from "../hooks/useSchoolSubscriptionTypes";

function ContactsInput({ contacts, setContacts }) {
  const addContact = () => {
    setContacts([
      ...contacts,
      {
        first_name: "",
        last_name: "",
        phone_number: "",
        email_address: "",
        notes: "",
      },
    ]);
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...contacts];
    newContacts[index][field] = value;
    setContacts(newContacts);
  };

  const removeContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
  };

  return (
    <Stack spacing={2}>
      {contacts.map((contact, index) => (
        <Stack key={index} spacing={1} direction="row" alignItems="center">
          <TextField
            label="First Name"
            value={contact.first_name}
            onChange={(e) => updateContact(index, "first_name", e.target.value)}
            size="small"
          />
          <TextField
            label="Last Name"
            value={contact.last_name}
            onChange={(e) => updateContact(index, "last_name", e.target.value)}
            size="small"
          />
          <TextField
            label="Phone"
            value={contact.phone_number}
            onChange={(e) =>
              updateContact(index, "phone_number", e.target.value)
            }
            size="small"
          />
          <TextField
            label="Email"
            value={contact.email_address}
            onChange={(e) =>
              updateContact(index, "email_address", e.target.value)
            }
            size="small"
          />
          <TextField
            label="Notes"
            value={contact.notes}
            onChange={(e) => updateContact(index, "notes", e.target.value)}
            size="small"
            fullWidth
          />
          <Button
            variant="outlined"
            color="error"
            onClick={() => removeContact(index)}
          >
            Remove
          </Button>
        </Stack>
      ))}
      <Button variant="contained" onClick={addContact}>
        Add Contact
      </Button>
    </Stack>
  );
}

function DomainNamesInput({ value, setValue }) {
  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]} // no predefined options
      value={value}
      onChange={(event, newValue) => {
        // Strip any leading '@' from each string
        const sanitized = newValue.map((v) =>
          v.startsWith("@") ? v.slice(1) : v,
        );
        setValue(sanitized);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            key={index}
            variant="outlined"
            label={option}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={"Email domains"}
          helperText="List possible domain names for school emails"
        />
      )}
    />
  );
}

export const RequestAccessDialog = ({
  open,
  setOpen,
  onComplete = () => {},
  initialSchool = null,
}) => {
  const [school, setSchool] = useState(initialSchool);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [domains, setDomains] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [licenceItems, setLicenceItems] = useState([]);
  const lastLicencePayloadRef = useRef("[]");
  const handleLicenceChange = useCallback((items) => {
    const key = JSON.stringify(items || []);
    if (lastLicencePayloadRef.current === key) return;
    lastLicencePayloadRef.current = key;
    setLicenceItems(items);
  }, []);

  const ApiClient = useApiClient();

  const requestWondeAccess = async () => {
    try {
      const result = await ApiClient.wonde.requestWondeAccess({
        school: school.id,
        domains,
        subscriptionType: selectedSubscription,
        contacts: JSON.stringify(contacts),
      });
      if (result.success) {
        if (licenceItems.length > 0) {
          try {
            await ApiClient.schools.updateSchoolSubscriptionLicences({
              school: school.id,
              items: licenceItems,
            });
          } catch (licenceErr) {
            console.error("Failed to save licences:", licenceErr);
            enqueueSnackbar("Access requested but failed to save licences", {
              variant: "warning",
              autoHideDuration: 3000,
            });
          }
        }
        enqueueSnackbar(result.message, {
          variant: "success",
          autoHideDuration: 3000,
        });
        onComplete({ refresh: false });
        handleClose();
      } else {
        enqueueSnackbar(result.message, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    setSchool(null);
    setOpen(false);
  };

  const { subscriptions } = useSchoolSubscriptionTypes(ApiClient);

  // Load subjects and year groups for pickers
  useEffect(() => {
    const loadCoreData = async () => {
      try {
        const res = await ApiClient.core.getSubjectsAndLevels();
        const subjectsArray = (res?.subjects ?? []).map((s) => ({
          id: s.id,
          name: s.name,
        }));
        setSubjects(subjectsArray);
        const yearGroupsArray = Array.from(
          new Map(
            (res?.educationLevels ?? [])
              .flatMap((lvl) => lvl.yearGroups ?? [])
              .map((yg) => [yg.id, yg]),
          ).values(),
        );
        setYearGroups(yearGroupsArray);
      } catch (e) {
        console.error(e);
      }
    };
    loadCoreData();
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"md"}>
      <DialogTitle>Request access to a new school through Wonde</DialogTitle>
      <DialogContent>
        <Stack direction={"column"} spacing={2} sx={{ mt: 2 }}>
          <SchoolSearchAutocomplete
            school={school}
            onChange={(_, value) => setSchool(value)}
          />
          <FormControl fullWidth>
            <InputLabel id="subscription-status-label">
              Subscription Status
            </InputLabel>
            <Select
              labelId="subscription-status-label"
              label={"Subscription Status"}
              value={selectedSubscription || ""}
              onChange={(e) => setSelectedSubscription(e.target.value)}
            >
              {subscriptions.map((sub) => (
                <MenuItem key={sub} value={sub}>
                  {sub.charAt(0).toUpperCase() + sub.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DomainNamesInput value={domains} setValue={setDomains} />
          <ContactsInput contacts={contacts} setContacts={setContacts} />
          <Divider sx={{ my: 2 }} />
          <LicenseUsageTable
            subjects={subjects}
            yearGroups={yearGroups}
            onChange={handleLicenceChange}
            licenseOptions={subscriptions}
            showLicencesUsed={false}
            showSaveButton={false}
            title="Licence allocation"
            titleVariant="h5"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button
          variant={"contained"}
          onClick={requestWondeAccess}
          disabled={
            domains.length === 0 ||
            !school ||
            !selectedSubscription ||
            licenceItems.length === 0
          }
        >
          Request Access
        </Button>
      </DialogActions>
    </Dialog>
  );
};
